import { useMemo } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import { normalizeApiResponse } from '../_normalize.js';
import { useInfiniteScrollSentinel } from '../_useInfiniteScrollSentinel.js';
import { retryServerErrorsOnly, stableFilterValues } from '../_utils.js';
import { useApiClient } from '../useApiClient.js';

import {
  type Notification,
  NOTIFICATIONS_POLL_INTERVAL_MS,
  type NotificationsFilters,
} from './useNotifications.js';

export const INFINITE_NOTIFICATIONS_QUERY_KEY = 'infiniteNotifications';

const DEFAULT_PAGE_SIZE = 20;

type UseInfiniteNotificationsParams = {
  enabled?: boolean;
  filters?: NotificationsFilters;
  pageSize?: number;
};

type NotificationsPage = {
  meta?: { pagination?: { count?: number; page?: number; pages?: number } };
};

const toFilterValue = (value?: boolean): string | undefined =>
  value === undefined ? undefined : String(value);

export const useInfiniteNotifications = ({
  enabled = true,
  filters,
  pageSize = DEFAULT_PAGE_SIZE,
}: UseInfiniteNotificationsParams = {}) => {
  const apiClient = useApiClient();

  const queryKey = useMemo(
    () => [INFINITE_NOTIFICATIONS_QUERY_KEY, { pageSize }, ...stableFilterValues(filters)],
    [pageSize, filters]
  );

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchNextPageError,
    isFetching,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const response = await apiClient.notifications.listNotificationsNotifications({
        pageNumber: pageParam,
        pageSize,
        filterIsRead: toFilterValue(filters?.isRead),
      });
      return response.data;
    },
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const pages = (lastPage as NotificationsPage | undefined)?.meta?.pagination?.pages;
      if (typeof pages !== 'number') return undefined;
      const next = lastPageParam + 1;
      return next <= pages ? next : undefined;
    },
    enabled,
    refetchInterval: NOTIFICATIONS_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    retry: retryServerErrorsOnly,
  });

  const normalizedData = useMemo<Notification[]>(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => {
      const normalized = normalizeApiResponse<Notification>(page);
      return Array.isArray(normalized) ? normalized : [normalized];
    });
  }, [data]);

  const sentinelRef = useInfiniteScrollSentinel({
    hasNextPage: !!hasNextPage,
    isFetching,
    fetchNextPage,
  });

  return useMemo(
    () => ({
      data,
      error,
      fetchNextPage,
      hasNextPage: !!hasNextPage,
      isFetchNextPageError,
      isFetching,
      isFetchingNextPage,
      normalizedData,
      refetch,
      sentinelRef,
    }),
    [
      data,
      error,
      fetchNextPage,
      hasNextPage,
      isFetchNextPageError,
      isFetching,
      isFetchingNextPage,
      normalizedData,
      refetch,
      sentinelRef,
    ]
  );
};
